import { generateClient, isCancelError } from "aws-amplify/api";

const client = generateClient();

export async function requestListBusinessTax(dispatch, payload) {
  try {
    const { page, businessID, limit = 10, nextTokens } = payload;

    dispatch({
      type: "REQUEST_LIST_BUSINESS_TAX",
      payload: { request: null, page },
    });

    let items = [];
    let nextToken = nextTokens || "start";

    const params = {
      businessID,
      limit,
      sortDirection: "DESC",
    };

    while (nextToken && items.length === 0) {
      if (nextToken && nextToken !== "start") {
        params.nextToken = nextToken;
      }

      const request = client.graphql({
        query: taxesByBusinessCreatedAt,
        variables: params,
      });
      dispatch({
        type: "REQUEST_LIST_BUSINESS_TAX",
        payload: { request, page },
      });

      // eslint-disable-next-line no-await-in-loop
      const res = await request;
      items = [...items, ...res.data.taxesByBusinessCreatedAt.items];
      nextToken = res.data.taxesByBusinessCreatedAt.nextToken;
    }

    const nextPage = page + 1;

    dispatch({
      type: "LIST_BUSINESS_TAX_SUCCESS",
      payload: {
        items,
        nextToken,
        nextPage,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_BUSINESS_TAX_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_BUSINESS_TAX_ERROR", error });
    }
  }
}

export async function requestGetBusinessTax(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_GET_BUSINESS_TAX",
      payload: { request: null },
    });
    const { id } = payload;

    const request = client.graphql({
      query: getBusinessTax,
      variables: { id },
    });
    dispatch({
      type: "REQUEST_GET_BUSINESS_TAX",
      payload: { request },
    });

    const res = await request;

    dispatch({
      type: "GET_BUSINESS_TAX_SUCCESS",
      payload: { tax: res.data.getBusinessTax },
    });
  } catch (error) {
    dispatch({ type: "GET_BUSINESS_TAX_ERROR", error });
  }
}

// One tax read straight from the API, leaving reducer state alone, for a page
// that opens the tax named in its URL.
export async function fetchBusinessTax(id) {
  const res = await client.graphql({
    query: getBusinessTax,
    variables: { id },
  });
  return res.data.getBusinessTax;
}

export async function requestCreateBusinessTax(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_CREATE_BUSINESS_TAX",
      payload: { request: null },
    });
    const { input } = payload;
    const request = client.graphql({
      query: createBusinessTax,
      variables: { input },
    });

    dispatch({ type: "REQUEST_CREATE_BUSINESS_TAX", payload: { request } });

    const res = await request;
    const tax = res.data.createBusinessTax;
    dispatch({
      type: "SUCCESS_CREATE_BUSINESS_TAX",
      payload: { tax },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "CANCEL_UPDATE_BUSINESS_TAX",
      });
    }
    dispatch({ type: "ERROR_CREATE_BUSINESS_TAX", error });
  }
}

export async function requestUpdateBusinessTax(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_UPDATE_BUSINESS_TAX",
      payload: { request: null },
    });
    const { input } = payload;
    const request = client.graphql({
      query: updateBusinessTax,
      variables: { input },
    });
    dispatch({ type: "REQUEST_UPDATE_BUSINESS_TAX", payload: { request } });

    const res = await request;
    const tax = res.data.updateBusinessTax;
    dispatch({
      type: "UPDATE_BUSINESS_TAX_SUCCESS",
      payload: { tax },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "CANCEL_UPDATE_BUSINESS_TAX",
      });
    }
    dispatch({ type: "ERROR_UPDATE_BUSINESS_TAX", error });
  }
}

export async function requestDeleteBusinessTax(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_DELETE_BUSINESS_TAX",
      payload: { request: null },
    });
    const { id } = payload;

    const request = client.graphql({
      query: deleteBusinessTax,
      variables: { input: { id } },
    });
    dispatch({ type: "REQUEST_DELETE_BUSINESS_TAX", payload: { request } });

    const res = await request;

    dispatch({
      type: "DELETE_BUSINESS_TAX_SUCCESS",
      payload: { id: res.data.deleteBusinessTax.id },
    });
  } catch (error) {
    dispatch({ type: "DELETE_BUSINESS_TAX_ERROR", error });
  }
}

export async function clearBusinessTax(dispatch) {
  dispatch({ type: "CLEAR_BUSINESS_TAX" });
}

export async function clearBusinessTaxError(dispatch) {
  dispatch({ type: "CLEAR_BUSINESS_TAX_ERROR" });
}

const taxesByBusinessCreatedAt = /* GraphQL */ `
  query TaxesByBusinessCreatedAt(
    $businessID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelBusinessTaxFilterInput
    $limit: Int
    $nextToken: String
  ) {
    taxesByBusinessCreatedAt(
      businessID: $businessID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        businessID
        inUse
        name
        computation
        type
        kind
        withholding
        amount
        accountID
        account {
          id
          businessID
          code
          name
          category
          type
          createdAt
          editable
          updatedAt
        }
        scope
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const getBusinessTax = /* GraphQL */ `
  query GetBusinessTax($id: ID!) {
    getBusinessTax(id: $id) {
      id
      businessID
      name
      computation
      type
      kind
      withholding
      amount
      accountID
      account {
        id
        businessID
        code
        name
        category
        type
        createdAt
        editable
        updatedAt
      }
      scope
      createdAt
      updatedAt
    }
  }
`;

const createBusinessTax = /* GraphQL */ `
  mutation CreateBusinessTax($input: CreateBusinessTaxInput!) {
    createBusinessTax(input: $input) {
      id
      businessID
      name
      computation
      type
      kind
      withholding
      amount
      accountID
      account {
        id
        businessID
        code
        name
        category
        type
        createdAt
        editable
        updatedAt
      }
      scope
      createdAt
      updatedAt
    }
  }
`;

const updateBusinessTax = /* GraphQL */ `
  mutation UpdateBusinessTax($input: UpdateBusinessTaxInput!) {
    updateBusinessTax(input: $input) {
      id
      businessID
      name
      computation
      type
      kind
      withholding
      amount
      accountID
      account {
        id
        businessID
        code
        name
        category
        type
        createdAt
        editable
        updatedAt
      }
      scope
      createdAt
      updatedAt
    }
  }
`;

const deleteBusinessTax = /* GraphQL */ `
  mutation DeleteBusinessTax($input: DeleteBusinessTaxInput!) {
    deleteBusinessTax(input: $input) {
      id
      businessID
      name
      computation
      type
      kind
      withholding
      amount
      accountID
      account {
        id
        businessID
        code
        name
        category
        type
        createdAt
        editable
        updatedAt
      }
      scope
      createdAt
      updatedAt
    }
  }
`;
